import { useMemo } from 'react';

import { buildRoutePingLayer, RoutePingDot } from '~components/Maps/v2/routePingLayer';
import useDeckOverlay from '~components/Maps/v2/useDeckOverlay';
import { Point } from '~pages/Admin/Geo/Distances/DistanceMap/catmullRomSpline';
import { hexToRgbaArray } from '~utils/utilFunctions';

// The Live Map draws one job at 8px; Truck Replay shows every truck on the order
// at once, where 8px dots merge into a blob.
const ROUTE_DOT_RADIUS_IN_PX = 2;
// A 3px border (the Live Map's) would swallow a 2px dot.
const ROUTE_DOT_BORDER_WIDTH_IN_PX = 1;
const HIGHLIGHTED_DOT_RADIUS_MULTIPLIER = 1.8;
const DIMMED_ALPHA = 255 * 0.25;
const FULL_ALPHA = 255;
const BORDER_ALPHA = 255 * 0.85;

interface ReplayDotTruck {
  color: string;
  jobId: string;
  points: Point[];
}

interface ReplayDotOverlayProps {
  dimmedJobIds: Set<string>;
  highlightColor: string | null;
  highlightedPoints: Point[] | null;
  map: google.maps.Map | null;
  trucks: ReplayDotTruck[];
}

const ReplayDotOverlay = ({
  dimmedJobIds,
  highlightColor,
  highlightedPoints,
  map,
  trucks,
}: ReplayDotOverlayProps) => {
  const layers = useMemo(() => {
    const dots: RoutePingDot[] = trucks.flatMap((truck) => {
      const alpha = dimmedJobIds.has(truck.jobId) ? DIMMED_ALPHA : FULL_ALPHA;

      return truck.points.map((position) => ({
        borderColor: hexToRgbaArray(truck.color, alpha),
        fillColor: hexToRgbaArray(truck.color, alpha),
        lat: position.lat,
        lineWidth: ROUTE_DOT_BORDER_WIDTH_IN_PX,
        lng: position.lng,
        radius: ROUTE_DOT_RADIUS_IN_PX,
      }));
    });

    if (highlightedPoints && highlightColor) {
      dots.push(
        ...highlightedPoints.map((position) => ({
          borderColor: hexToRgbaArray('#FFFFFF', BORDER_ALPHA),
          fillColor: hexToRgbaArray(highlightColor, FULL_ALPHA),
          lat: position.lat,
          lineWidth: ROUTE_DOT_BORDER_WIDTH_IN_PX,
          lng: position.lng,
          radius: ROUTE_DOT_RADIUS_IN_PX * HIGHLIGHTED_DOT_RADIUS_MULTIPLIER,
        })),
      );
    }

    const layer = buildRoutePingLayer('truck-replay-route-dots', dots);

    return layer ? [layer] : [];
  }, [dimmedJobIds, highlightColor, highlightedPoints, trucks]);

  useDeckOverlay({ layers, map });

  return null;
};

export default ReplayDotOverlay;
